import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, MessageCircle, XCircle } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { shopApi, fmt } from "../../lib/shopApi";

const STATUS_LABELS = {
  awaiting_payment: "Awaiting Payment",
  pending_payment: "Payment Pending",
  paid: "Paid",
  packed: "Packed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  payment_failed: "Payment Failed",
  payment_error: "Payment Error",
  new: "New",
};

const OrderConfirmationPage = () => {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const paymentResult = params.get("payment");
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [whatsapp, setWhatsapp] = useState("27788583255");

  useEffect(() => {
    shopApi.get("/shop/config").then((r) => setWhatsapp(r.data.whatsapp)).catch(() => {});
    const load = () =>
      shopApi
        .get(`/shop/orders/${orderNumber}`)
        .then((r) => setOrder(r.data))
        .catch(() => setNotFound(true));
    load();
    // Refresh once after a payment redirect so the webhook has a moment to land
    const t = paymentResult ? setTimeout(load, 4000) : null;
    return () => t && clearTimeout(t);
  }, [orderNumber, paymentResult]);

  if (notFound) {
    return (
      <ShopLayout title="Order Not Found | Loctician Carlocs">
        <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="order-not-found">
          <p className="text-[#F5EFE3]/50 mb-6">We couldn't find that order.</p>
          <Link to="/shop" className="text-[#C9A24B] underline underline-offset-4">Back to the shop</Link>
        </div>
      </ShopLayout>
    );
  }

  if (!order) {
    return (
      <ShopLayout title="Loading Order | Loctician Carlocs">
        <div className="py-40 text-center text-[#F5EFE3]/40 text-sm">Loading your order…</div>
      </ShopLayout>
    );
  }

  const isPaid = ["paid", "packed", "shipped", "delivered"].includes(order.status);
  const failed = ["payment_failed", "payment_error"].includes(order.status) || paymentResult === "failed";

  return (
    <ShopLayout title={`Order ${order.order_number} | Loctician Carlocs`}>
      <div className="max-w-3xl mx-auto px-5 py-14 md:py-20" data-testid="order-confirmation">
        <div className="text-center mb-10">
          {failed ? (
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-5" />
          ) : isPaid ? (
            <CheckCircle2 className="w-14 h-14 text-[#C9A24B] mx-auto mb-5" />
          ) : (
            <Clock className="w-14 h-14 text-[#C9A24B] mx-auto mb-5" />
          )}
          <h1 className="font-serif text-3xl md:text-5xl mb-3">
            {failed ? (
              <>Payment <span className="italic text-red-400">unsuccessful</span></>
            ) : (
              <>Thank you, <span className="italic text-[#C9A24B]">{order.customer.name.split(" ")[0]}</span></>
            )}
          </h1>
          <p className="text-[#F5EFE3]/60 font-light">
            {failed
              ? "Your order is saved — you can try paying again."
              : isPaid
                ? "Your payment is confirmed and your order is being prepared."
                : "Your order is saved — complete your card payment to confirm it."}
          </p>
        </div>

        <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 md:p-8 mb-6">
          {order.has_made_to_order && (
            <p data-testid="order-lead-time" className="border border-[#C9A24B]/40 bg-[#C9A24B]/10 text-[#C9A24B] text-xs leading-relaxed rounded-xl px-4 py-3 mb-6">
              This order includes made-to-order locs extensions — handmade in 7-15 days. We'll email you at every step, and your delivery option begins once they're ready.
            </p>
          )}
          <div className="flex flex-wrap justify-between gap-3 mb-6 pb-6 border-b border-[#C9A24B]/10">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#F5EFE3]/40 mb-1">Order Number</p>
              <p className="font-serif text-2xl text-[#C9A24B]" data-testid="order-number">{order.order_number}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-[#F5EFE3]/40 mb-1">Status</p>
              <p className="font-semibold" data-testid="order-status">{STATUS_LABELS[order.status] || order.status}</p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-[#F5EFE3]/70">
                  {i.name}{i.size_label ? ` (${i.size_label})` : ""} × {i.qty}
                  {Object.values(i.options || {}).length > 0 && (
                    <span className="block text-[#F5EFE3]/45 text-xs mt-0.5">
                      {[i.options.length, i.options.color, i.options.thickness].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
                <span>{fmt(i.line_total_cents)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-[#C9A24B]/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-[#F5EFE3]/60"><span>Subtotal</span><span>{fmt(order.subtotal_cents)}</span></div>
            {order.discount_cents > 0 && (
              <div className="flex justify-between text-[#C9A24B]"><span>Discount ({order.discount_code})</span><span>-{fmt(order.discount_cents)}</span></div>
            )}
            <div className="flex justify-between text-[#F5EFE3]/60">
              <span>
                {order.delivery_method === "collection"
                  ? "Salon Pick Up — Johannesburg CBD (same day)"
                  : order.delivery_method === "uber"
                    ? "Uber — same-day delivery"
                    : "Paxi — delivered in 3-5 days"}
              </span>
              <span>{order.delivery_fee_cents === 0 ? "Free" : fmt(order.delivery_fee_cents)}</span>
            </div>
            <div className="flex justify-between font-semibold text-lg pt-2"><span>Total</span><span className="text-[#C9A24B]" data-testid="order-total">{fmt(order.total_cents)}</span></div>
          </div>
        </div>

        <p className="text-[#F5EFE3]/45 text-sm text-center mb-8">
          A confirmation email is on its way to <span className="text-[#F5EFE3]/70">{order.customer.email}</span>.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={`/track?order=${order.order_number}`}
            data-testid="track-order-btn"
            className="inline-flex items-center justify-center gap-2 border border-[#C9A24B]/40 text-[#C9A24B] px-8 py-4 rounded-full text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A24B] hover:text-[#1C1C1C] transition-all duration-300"
          >
            Track This Order
          </Link>
          <a
            href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi Carlocs! Question about my order ${order.order_number}.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 border border-[#C9A24B]/40 text-[#C9A24B] px-8 py-4 rounded-full text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A24B] hover:text-[#1C1C1C] transition-all duration-300"
          >
            <MessageCircle className="w-4 h-4" /> Order Help
          </a>
          <Link
            to="/shop"
            data-testid="continue-shopping-btn"
            className="inline-flex items-center justify-center bg-[#C9A24B] text-[#1C1C1C] px-8 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-transform duration-300"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </ShopLayout>
  );
};

export default OrderConfirmationPage;
